  <template>
  <div class="payment-panel">
    <div class="payment-methods">
    <h5>Payment Methods</h5>
      <!-- Payment methods -->
      <div :class="['payment-methods__card', { primary: isPrimaryMethod(method) }]" v-for="method in paymentMethods" :key="method.id">
        <div :class="['payment-methods__card__type', method.card.brand]" v-html="`${uppercaseFirst(method.card.brand)} ending in ${method.card.last4}`"/>
        <div v-html="`Expires ${method.card.exp_month}/${method.card.exp_year}`"/>

        <!-- Delete, make primary buttons -->
        <div class="payment-methods__actions">
          <template v-if="!isPrimaryMethod(method)">
            <button @click="deleteCard(method.id, $event)">Delete</button>
            <button @click="makePrimary(method.id, $event)">Make primary</button>
          </template>
          <p v-else>Primary</p>
        </div>
      </div>
      <!-- Error about stripe customer ID not existing -->
      <p v-if="!currentUserProfile.stripe.customerId">Error: stripe customer ID non-existant</p>
      <!-- Loading message -->
      <p v-else-if="loadingPaymentMethods">Loading...</p>
      <template v-else>
        <!-- No payment methods -->
        <p v-if="!paymentMethods.length">No payment methods on file</p>
        <button class="payment-methods__add-btn" @click="showAddPaymentMethod=true" v-if="!showAddPaymentMethod">Add payment method</button>
      </template>
    </div>
    <div class="add-payment-method" v-show="showAddPaymentMethod && !loadingPaymentMethods">
      <h5>Enter Your Payment Details</h5>
      <!-- Stripe Card Element & add payment method -->
      <form @submit.prevent id="add-card-form" v-show="!loadingPaymentMethods">
        <div id="card-element"></div>
        <button id="card-button" @click="submitStripeAddCard" :disabled="!addCardEnabled">Add Payment Method</button>
      </form>
    </div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import { mapGetters } from 'vuex';
import { getClientPaymentMethods } from '@/library/userFunctions'
import { uppercaseFirst } from '@/library/utilityFunctions'
import * as fb from '@/library/firebase'
const detachPaymentMethod = fb.funcs.httpsCallable('detachPaymentMethod')

export default {
  name: 'PaymentPanel',
  data() {
    return {
      addCardEnabled: false,
      cardElement: null,
      showAddPaymentMethod: false,
      paymentMethods: [],
      loadingPaymentMethods: true,
      stripe: null
    };
  },
  async mounted() {
    // Initialize Stripe
    this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
    setTimeout(() => {
      if (this.currentUserProfile?.stripe?.customerId) {
        getClientPaymentMethods(this.currentUserProfile.stripe.customerId).then(res => {
          this.paymentMethods = res.data;
          this.loadingPaymentMethods = false;
        })
        this.createAndMountFormElements()
      }
    }, 2000)
  },
  computed: {
    ...mapGetters([
      'currentUserProfile'
    ]),
  },
  methods: {
    uppercaseFirst,
    isPrimaryMethod(method) {
      return method.id === this.currentUserProfile.stripe.paymentMethodId
    },
    async createAndMountFormElements() {
      if (!this.stripe?.elements) {
        return
      }
      const elements = this.stripe.elements()

      var style = {
        base: {
          color: '#32325d',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      }

      if (document.getElementById('card-element')) {
        // Create an instance of the card Element.
        this.cardElement = elements.create('card', {style: style})

        // Add an instance of the card Element into the `card-element` <div>.
        this.cardElement.mount('#card-element')

        // When card element changes, enable or disable the sign up button
        this.cardElement.on('change', event => {
          this.addCardEnabled = false
          if (event.complete) {
            this.addCardEnabled = true
          }
        })
      }
    },
    async makePrimary(id, event) {
      try {
        await fb.usersCollection.doc(this.currentUserProfile.uid).update({
          'stripe.paymentMethodId': id
        })
        this.currentUserProfile.stripe.paymentMethodId = id;
        document.querySelector('.payment-methods__card.primary').classList.remove('primary')
        event.target.closest('.payment-methods__card').classList.add('primary')
      }
      catch(err) {
        this.$notify({
          group: 'default',
          title: 'Error',
          text: err,
          type: 'error'
        })
      }
    },
    async deleteCard(id, event) {
      try {
        await detachPaymentMethod(id)
        event.target.closest('.payment-methods').removeChild(event.target.closest('.payment-methods__card'))
        this.$notify({
          group: 'default',
          title: 'Success',
          text: 'Card successfully deleted',
          type: 'success'
        })
      }
      catch(err) {
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: err,
          type: 'error'
        })
      }
    },
    async submitStripeAddCard(){
      this.addCardEnabled = false
      const setupIntent = await fb.funcs.httpsCallable('createSetupIntent')({
        uid: this.currentUserProfile.uid,
        stripeCustomerId: this.currentUserProfile.stripe.customerId
      })
      this.stripe.confirmCardSetup(
        setupIntent.data.client_secret,
        {
          payment_method: {
            card: this.cardElement,
            billing_details: {
              name: this.currentUserProfile.firstName + ' ' + this.currentUserProfile.lastName,
              email: this.currentUserProfile.email
            }
          },
        }
      ).then(result => {
        if (result.error) {
          this.stripeErrorMsg = result.error.message
          this.addCardEnabled = true
          this.$notify({
            group: 'default',
            title: 'Error',
            text: result.error,
            type: 'error'
          })
          this.addCardEnabled = true
        } else {
          // The setup has succeeded
          this.$notify({
            group: 'default',
            title: 'Success',
            text: `Status: ${result.setupIntent.status}`
          })
          this.addCardEnabled = true

          // Fetch updated payment methods
          this.loadingPaymentMethods = true
          this.cardElement.clear()
          getClientPaymentMethods(this.currentUserProfile.stripe.customerId).then(res => {
            this.paymentMethods = res.data
            this.loadingPaymentMethods = false
          })
        }
      })
    },
  }
}
</script>

<style lang="scss">
.payment-methods {
  &__card {
    border: solid 1px #ccc;
    display: flex;
    align-items: center;
    padding: 20px;
    margin: 0 0 20px;
    justify-content: space-between;
    @media ($sm-down) {
     display: block;
    }
    &.primary {
      border: solid 4px $secondary;
    }

    &__type {
      background-size: 32px;
      padding-left: 50px;
      background-repeat: no-repeat;
      background-position: 0 50%;
      @media ($sm-down) {
        padding-bottom: .5rem;
      }
      &.amex {
        background-image: url(~@/assets/img/card-amex.svg);
      }
      &.discover {
        background-image: url(~@/assets/img/card-discover.svg);
      }
      &.visa {
        background-image: url(~@/assets/img/card-visa.svg);
      }
      &.mastercard {
        background-image: url(~@/assets/img/card-mastercard.svg);
      }
    }

    div {
      margin-right: 20px;
    }

  }
  &__actions {
    min-width: 180px;
    text-align: right;
    button {
      margin-right: 10px;
      &:last-child {
        margin-right: 0;
      }
    }
  }
}
.add-payment-method {
  margin: 20px auto;
  padding: 20px;
  border: solid 1px $c-border;
  background: top 10px right 14px no-repeat url(~@/assets/img/logo-stripe.png);
  background-size: 130px;

  h5 {
    margin: 0 0 1rem;
    font-size: 1.2rem;
    max-width: calc(100% - 110px);

  }

  #card-element {
    margin: 1rem 0;
    border: solid 1px $c-border;
    padding: 1rem;
  }

  button {
    background-color: #eee;
    border: solid 1px $c-border;
    line-height: 2;
    padding: 0 .5rem;
    border-radius: .25rem;
  }
}
</style>